function _typeof(obj) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (obj) { return typeof obj; } : function (obj) { return obj && "function" == typeof Symbol && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }, _typeof(obj); }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return _typeof(key) === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (_typeof(input) !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (_typeof(res) !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { serializeOptions, noop, toLower, triggerOnKeys, KeyCodes } from '../../utils';
import manageFocus from './focus';

/**
 *
 * @name TabBar
 * @type Function
 * @description A control component for navigating among multiple tabs
 * @param {Boolean} [vertical] - A boolean setting the `className` of the `ul` to 'horizontal' (default), or 'vertical', which determines the alignment of the tabs (optional, default `false`)
 * @param {Array} options - An array of tab values (strings or key-value pairs)
 * @param {String|Number} value - The value of the current tab
 * @param {Function} [onChange] - A function called with the new value when a tab is clicked
 * @param {String} [activeClassName] - The class of the active tab, (optional, default `active`)
 * @example
 *
 * function ShowTabs ({ currentTab, setCurrentTab }) {
 *   return (
 *     <div>
 *       <TabBar
 *         options={['Tab 1', 'Tab 2']}
 *         value={currentTab}
 *         onChange={setCurrentTab}
 *       />
 *     </div>
 *   )
 * }
 */

var propTypes = {
  vertical: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.shape({
    key: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
  })])).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChange: PropTypes.func,
  className: PropTypes.string,
  activeClassName: PropTypes.string
};
var defaultProps = {
  vertical: false,
  onChange: noop,
  className: '',
  activeClassName: 'active'
};
function TabBar(_ref) {
  var vertical = _ref.vertical,
    options = _ref.options,
    value = _ref.value,
    onChange = _ref.onChange,
    className = _ref.className,
    activeClassName = _ref.activeClassName;
  var optionObjects = serializeOptions(options);
  var alignment = vertical ? 'vertical' : 'horizontal';
  return /*#__PURE__*/React.createElement("ul", {
    className: classnames('tabs', "".concat(alignment, "-tabs"), className),
    role: "tablist",
    "aria-orientation": alignment
  }, optionObjects.map(function (_ref2) {
    var key = _ref2.key,
      optionValue = _ref2.value;
    var isActive = optionValue === value;
    return /*#__PURE__*/React.createElement("li", {
      className: classnames(_defineProperty({}, activeClassName, isActive)),
      key: key,
      role: "presentation",
      onKeyDown: function onKeyDown(e) {
        return manageFocus(e, {
          vertical: vertical
        });
      }
    }, /*#__PURE__*/React.createElement("a", {
      id: 'tab-' + toLower(optionValue) // allow sections to reference tab using `aria-labelledby`
      ,
      onClick: function onClick() {
        onChange(optionValue);
      },
      onKeyPress: triggerOnKeys(function () {
        onChange(optionValue);
      }, [KeyCodes.ENTER, KeyCodes.SPACE]),
      tabIndex: isActive ? '0' : '-1' // remove inactive tabs from tab order (controlled with arrow keys)
      ,
      role: "tab",
      "aria-selected": isActive
    }, key));
  }));
}
TabBar.propTypes = propTypes;
TabBar.defaultProps = defaultProps;
export default TabBar;